import { useContext, useEffect, useRef } from 'react'
import moment from 'moment'
import { SchedulingWizardContext } from 'app/components/scheduling/SchedulingWizard'
import type { PatientJson } from 'app/types/entities'
import { bucketForAppointmentType, normalizeProviderIds } from 'lib/preferredProviders'
import type { PreferredProvider } from 'lib/preferredProviders'

/**
 * Null-rendering effect component that drives the SchedulingWizard from inside its
 * context for the in-app (staff-initiated) booking flow:
 *  - auto-skips the patient-status step (step 1) as "Existing Patient"
 *  - prefills the patient's identity + insurance from the linked Patient record
 *  - preselects the preferred provider/hygienist for the chosen appointment type
 *
 * Must be rendered as a child of <SchedulingWizard> (i.e. within its context).
 */
export function InAppPrefillController({
  patient,
  preferredProvider,
  preferredHygienist,
}: {
  patient?: PatientJson
  preferredProvider?: PreferredProvider
  preferredHygienist?: PreferredProvider
}): null {
  const { currentStep, setPatientType, setPatient, setSelectedInsurance, apptType, providers, setProvider } =
    useContext(SchedulingWizardContext)

  // Skip the patient-status step (re-skips if the user hits Back to step 1).
  useEffect(() => {
    if (currentStep === 1) setPatientType(`Existing Patient`)
  }, [currentStep, setPatientType])

  // Prefill patient identity + insurance once the patient loads.
  useEffect(() => {
    if (!patient) return
    setPatient({
      firstName: patient.firstName || ``,
      lastName: patient.lastName || ``,
      email: patient.email || ``,
      phone: patient.cellPhone || ``,
      dob: patient.birthDate ? moment(patient.birthDate).format(`MM/DD/YYYY`) : ``,
    })
    if (patient.insuranceProvider) setSelectedInsurance(patient.insuranceProvider)
  }, [patient, setPatient, setSelectedInsurance])

  const appliedTypeRef = useRef<string | undefined>(undefined)

  // Preselect the preferred person for the chosen appointment type — at most once per
  // type (tracked in appliedTypeRef). This is deliberate: once we've applied the
  // preselection for a type, we do NOT retry on later renders (e.g. a providers refetch
  // after month navigation, or preferred data arriving late), so a subsequent manual
  // provider pick is never silently clobbered. The trade-off is that if the preferred
  // provider only appears after the first successful application, it won't be re-applied.
  useEffect(() => {
    if (!apptType || !providers?.length) return
    if (appliedTypeRef.current === apptType.id) return
    const bucket = bucketForAppointmentType(apptType)
    const preferred = bucket === `hygiene` ? preferredHygienist : preferredProvider
    if (!preferred) return // preferred not loaded yet; retry when it arrives (ref not set)
    // preferred.providerId is a normalized bare resource id; provider records may
    // carry it as vendorId (bare) or id (K-<locationKey>- prefixed), either
    // possibly comma-joined — normalize both sides before comparing.
    const match = providers.find((p) =>
      [p.vendorId, p.id].some((candidate) => normalizeProviderIds(candidate).includes(preferred.providerId)),
    )
    if (match) {
      setProvider(match)
      appliedTypeRef.current = apptType.id
    }
  }, [apptType, providers, preferredProvider, preferredHygienist, setProvider])

  return null
}
